import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Edit2,
  Trash2,
  Wallet,
  X,
  Eye,
  Clock3,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  Rows3,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { api, fmtINR, formatApiError } from "../lib/api";
import MoneyInput from "../components/ui/MoneyInput";
import { assetTotal, assetInvested, isPerUnit } from "../lib/assets";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import UiDateField from "../components/ui/DateField";
import "../styles/date.css";

// Full label map (insurance is managed in its own tab but still rendered here
// because investment policies surface a read-only cash-value row).
const CATEGORY_LABEL = {
  mutual_funds: "Mutual Funds",
  stocks: "Stocks",
  bank: "Bank Accounts",
  fd: "Fixed Deposit",
  rd: "Recurring Deposit",
  mis: "Monthly Income Scheme",
  bonds: "Bonds",
  gold: "Gold",
  silver: "Silver",
  crypto: "Crypto",
  real_estate: "Real Estate",
  vehicle: "Vehicle",
  business: "Business",
  insurance: "Insurance",
  epf: "EPF / PPF",
  nps: "NPS",
  cash: "Cash",
  collectibles: "Collectibles",
  digital: "Digital Assets",
  other: "Other",
};

// Categories the user can add here (insurance lives in the Insurance tab).
const ADDABLE_CATEGORIES = [
  "mutual_funds",
  "stocks",
  "bank",
  "fd",
  "rd",
  "mis",
  "bonds",
  "gold",
  "silver",
  "crypto",
  "real_estate",
  "vehicle",
  "business",
  "epf",
  "nps",
  "cash",
  "collectibles",
  "digital",
  "other",
].map((v) => ({ value: v, label: CATEGORY_LABEL[v] }));

// Per-type field configuration. Keys default to false/undefined when omitted.
//   quantity      → label for a unit/quantity field (enables it)
//   sip           → show monthly contribution + step-up block (with label)
//   tenure        → show the tenure/maturity block by default
//   returnLabel   → label for the expected-return input
//   purchaseLabel → label for the "amount invested / cost" field
//   hidePurchase  → omit purchase value & date (e.g. a bank balance)
//   liquidDefault → default the is_liquid flag on
const ASSET_TYPE_CONFIG = {
  mutual_funds: {
    sip: true,
    sipLabel: "Monthly SIP (₹)",
    returnLabel: "Expected Return %",
  },
  stocks: { returnLabel: "Expected Return %", quantity: "Number of Shares" },
  bank: {
    returnLabel: "Interest Rate %",
    hidePurchase: true,
    liquidDefault: true,
  },
  fd: {
    tenure: true,
    returnLabel: "Interest Rate %",
    purchaseLabel: "Principal Invested (₹)",
  },
  rd: {
    tenure: true,
    sip: true,
    sipLabel: "Monthly Deposit (₹)",
    returnLabel: "Interest Rate %",
    purchaseLabel: "Invested So Far (₹)",
  },
  // Monthly Income Scheme (e.g. Post Office MIS): pays a fixed monthly income.
  // Only the amount invested is asked (used as the value); monthly income is
  // derived = value × interest rate ÷ 12.
  mis: {
    tenure: true,
    hideValue: true,
    returnLabel: "Interest Rate % p.a.",
    purchaseLabel: "Amount Invested (₹)",
  },
  bonds: { tenure: true, returnLabel: "Coupon / YTM %" },
  gold: { returnLabel: "Expected Return %", quantity: "Gold (grams)" },
  silver: { returnLabel: "Expected Return %", quantity: "Silver (grams)" },
  crypto: {
    returnLabel: "Expected Return %",
    quantity: "Crypto Quantity",
    liquidDefault: false,
  },
  real_estate: { returnLabel: "Appreciation % p.a." },
  vehicle: { returnLabel: "Depreciation % p.a." },
  business: { returnLabel: "Expected Return %" },
  epf: {
    sip: true,
    sipLabel: "Monthly Contribution (₹)",
    returnLabel: "Interest Rate %",
  },
  nps: {
    sip: true,
    sipLabel: "Monthly Contribution (₹)",
    returnLabel: "Expected Return %",
  },
  cash: {
    returnLabel: "Expected Return %",
    hidePurchase: true,
    liquidDefault: true,
  },
  collectibles: { returnLabel: "Expected Return %", quantity: "Quantity" },
  digital: { returnLabel: "Expected Return %" },
  other: { returnLabel: "Expected Return %" },
};

const cfgFor = (cat) => ASSET_TYPE_CONFIG[cat] || ASSET_TYPE_CONFIG.other;
const isSynthetic = (a) => a?.synthetic === true || a?.source === "insurance";

const todayISO = () => new Date().toISOString().slice(0, 10);

// Tenure = (maturity − start) in whole years. Start defaults to today when
// blank. Returns { start, tenure } with tenure undefined if it can't be derived.
function deriveTenure(startDate, endDate, tenureYears) {
  const start = startDate || todayISO();
  let tenure = tenureYears ? Number(tenureYears) : undefined;
  if (!tenure && endDate) {
    const yrs =
      (new Date(endDate) - new Date(start)) / (365.25 * 24 * 3600 * 1000);
    if (yrs > 0) tenure = Math.max(1, Math.round(yrs));
  }
  return { start, tenure };
}

const PAGE_SIZE_OPTIONS = [10, 25, 50];

function cagr(purchase, current, dateStr) {
  const p = Number(purchase);
  const c = Number(current);
  if (!p || !c || p <= 0 || !dateStr) return null;
  const years =
    (Date.now() - new Date(dateStr).getTime()) / (365.25 * 24 * 3600 * 1000);
  if (years < 0.08) return null; // < ~1 month, not meaningful
  return (Math.pow(c / p, 1 / years) - 1) * 100;
}

export default function Assets() {
  const { viewOnly } = useAuth();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/assets");
      setAssets(data.assets || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    try {
      await api.delete(`/assets/${id}`);
      toast.success("Asset deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const total = assets.reduce((s, a) => s + assetTotal(a), 0);

  const totalPages = Math.max(1, Math.ceil(assets.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pagedAssets = assets.slice(start, start + pageSize);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (safePage > 3) pages.push("…");
      for (
        let i = Math.max(2, safePage - 1);
        i <= Math.min(totalPages - 1, safePage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (safePage < totalPages - 2) pages.push("…");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">
            Your portfolio
          </p>
          <h1
            data-testid="assets-title"
            className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Assets
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Total:{" "}
            <span className="num-mono font-bold text-emerald-600">
              {fmtINR(total)}
            </span>{" "}
            across {assets.length} holdings.
          </p>
        </div>
        {!viewOnly && (
          <div className="flex items-center gap-2">
            <Link
              data-testid="open-bulk-asset"
              to="/dashboard/bulk-add/assets"
              className="btn-secondary"
            >
              <Rows3 className="h-4 w-4" /> Bulk add
            </Link>
            <button
              data-testid="open-add-asset"
              onClick={() => {
                setEditing(null);
                setShowModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="h-4 w-4" /> Add asset
            </button>
          </div>
        )}
      </div>

      <div className="card-soft overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-slate-500">
            Loading…
          </div>
        ) : assets.length === 0 ? (
          <div className="p-12 text-center">
            <Wallet className="h-10 w-10 text-slate-300 mx-auto" />
            <h3 className="mt-3 font-display font-bold">No assets yet</h3>
            <p className="mt-1 text-sm text-slate-500">
              {viewOnly
                ? "This client has no assets yet."
                : "Add your first asset to start building your net worth."}
            </p>
            {!viewOnly && (
              <button
                onClick={() => {
                  setEditing(null);
                  setShowModal(true);
                }}
                className="btn-primary mt-4 !text-xs"
              >
                <Plus className="h-3 w-3" /> Add asset
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="w-full">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 dark:border-slate-800">
                  <th className="text-left px-6 py-4 font-semibold">Name</th>
                  <th className="text-left px-6 py-4 font-semibold hidden md:table-cell">
                    Category
                  </th>
                  <th className="text-left px-6 py-4 font-semibold hidden lg:table-cell">
                    Institution
                  </th>
                  <th className="text-right px-6 py-4 font-semibold">Value</th>
                  <th className="text-right px-6 py-4 font-semibold hidden md:table-cell">
                    Return
                  </th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {pagedAssets.map((a) => {
                  const syn = isSynthetic(a);
                  return (
                    <tr
                      key={a.id}
                      data-testid={`asset-row-${a.id}`}
                      className="border-b border-slate-100 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                    >
                      <td className="px-6 py-4">
                        <div className="font-semibold text-slate-900 dark:text-white">
                          {a.name}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {syn && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300">
                              <ShieldCheck className="h-3 w-3" />
                              Insurance
                            </span>
                          )}
                          {a.has_tenure && !syn && (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                              <Clock3 className="h-3 w-3" />
                              Tenured
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 md:hidden">
                          {CATEGORY_LABEL[a.category] || a.category}
                        </div>
                      </td>
                      <td className="px-6 py-4 hidden md:table-cell">
                        <span className="text-xs font-medium rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-1 text-slate-700 dark:text-slate-300">
                          {CATEGORY_LABEL[a.category] || a.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-300 hidden lg:table-cell">
                        {a.institution || "—"}
                      </td>
                      <td className="px-6 py-4 text-right num-mono font-semibold text-slate-900 dark:text-white">
                        {fmtINR(assetTotal(a))}
                      </td>
                      <td className="px-6 py-4 text-right text-xs hidden md:table-cell">
                        <span
                          className={
                            a.expected_return >= 0
                              ? "text-emerald-600 font-semibold"
                              : "text-rose-600 font-semibold"
                          }
                        >
                          {a.expected_return ? `${a.expected_return}%` : "—"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right whitespace-nowrap">
                        <button
                          onClick={() => setViewing(a)}
                          className="p-1.5 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-500/10 text-slate-500 hover:text-sky-600"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        {syn ? (
                          <Link
                            to="/dashboard/insurance"
                            title="Managed in the Insurance tab"
                            className="inline-flex p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-sky-600"
                          >
                            <ShieldCheck className="h-3.5 w-3.5" />
                          </Link>
                        ) : viewOnly ? null : (
                          <>
                            <button
                              data-testid={`edit-asset-${a.id}`}
                              onClick={() => {
                                setEditing(a);
                                setShowModal(true);
                              }}
                              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-emerald-600"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                            </button>
                            <button
                              data-testid={`delete-asset-${a.id}`}
                              onClick={() => onDelete(a.id)}
                              className="p-1.5 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-500/10 text-slate-500 hover:text-rose-600"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination footer */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span>
                  Showing{" "}
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {start + 1}–{Math.min(start + pageSize, assets.length)}
                  </span>{" "}
                  of{" "}
                  <span className="font-medium text-slate-700 dark:text-slate-300">
                    {assets.length}
                  </span>
                </span>
                <span className="hidden sm:inline text-slate-300 dark:text-slate-700">
                  |
                </span>
                <div className="hidden sm:flex items-center gap-1.5">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) =>
                      handlePageSizeChange(Number(e.target.value))
                    }
                    className="text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {getPageNumbers().map((p, i) =>
                  p === "…" ? (
                    <span
                      key={`ellipsis-${i}`}
                      className="px-1 text-xs text-slate-400 select-none"
                    >
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`min-w-[32px] h-8 rounded-lg text-xs font-medium transition ${
                        safePage === p
                          ? "bg-emerald-500 text-white shadow-sm"
                          : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                    >
                      {p}
                    </button>
                  ),
                )}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition"
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {showModal && (
        <AssetModal
          asset={editing}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            load();
          }}
        />
      )}
      {viewing && (
        <AssetViewModal asset={viewing} onClose={() => setViewing(null)} />
      )}
    </DashboardLayout>
  );
}

function AssetModal({ asset, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: asset?.name || "",
    category: asset?.category || "mutual_funds",
    value: asset?.value || "",
    purchase_value: asset?.purchase_value || "",
    purchase_date: asset?.purchase_date
      ? String(asset.purchase_date).slice(0, 10)
      : "",
    quantity: asset?.quantity || "",
    expected_return: asset?.expected_return || "",
    institution: asset?.institution || "",
    notes: asset?.notes || "",
    has_tenure: asset?.has_tenure || false,
    start_date: asset?.start_date ? String(asset.start_date).slice(0, 10) : "",
    maturity_date: asset?.maturity_date
      ? String(asset.maturity_date).slice(0, 10)
      : "",
    tenure_years: asset?.tenure_years || "",
    monthly_contribution: asset?.monthly_contribution || "",
    contribution_step_up: asset?.contribution_step_up || "",
    is_liquid: asset?.is_liquid ?? false,
  });
  const [pickers, setPickers] = useState({});
  const [saving, setSaving] = useState(false);

  const cfg = cfgFor(form.category);
  const showQuantity = !!cfg.quantity;
  const showSIP = !!cfg.sip;
  const showTenure = !!cfg.tenure || form.has_tenure;
  const showPurchase = !cfg.hidePurchase;
  // For MIS the principal doesn't change, so we only ask for the amount
  // invested and use it as the current value on save.
  const showValue = !cfg.hideValue;

  // When switching category, snap tenure / liquid defaults to the new type.
  const onCategoryChange = (category) => {
    const c = cfgFor(category);
    setForm((f) => ({
      ...f,
      category,
      has_tenure: c.tenure ? true : f.has_tenure,
      is_liquid: c.liquidDefault ? true : f.is_liquid,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // MIS has no separate current value — the amount invested IS the value.
      const rawValue = cfg.hideValue ? form.purchase_value : form.value;
      const payload = {
        name: form.name,
        category: form.category,
        value: Number(String(rawValue || 0).replace(/,/g, "")),
        institution: form.institution || undefined,
        notes: form.notes || undefined,
        expected_return: form.expected_return
          ? Number(form.expected_return)
          : undefined,
        is_liquid: !!form.is_liquid,
      };
      if (showPurchase) {
        payload.purchase_value = form.purchase_value
          ? Number(String(form.purchase_value).replace(/,/g, ""))
          : undefined;
        payload.purchase_date = form.purchase_date || undefined;
      }
      if (showQuantity && form.quantity)
        payload.quantity = Number(form.quantity);
      if (showSIP) {
        payload.monthly_contribution = form.monthly_contribution
          ? Number(String(form.monthly_contribution).replace(/,/g, ""))
          : 0;
        payload.contribution_step_up = form.contribution_step_up
          ? Number(form.contribution_step_up)
          : 0;
      }
      payload.has_tenure = !!showTenure;
      if (showTenure) {
        const { start, tenure } = deriveTenure(
          form.start_date,
          form.maturity_date,
          form.tenure_years,
        );
        payload.start_date = start; // defaults to today when blank
        payload.maturity_date = form.maturity_date || undefined;
        payload.tenure_years = tenure;
      }
      Object.keys(payload).forEach(
        (k) => payload[k] === "" && delete payload[k],
      );
      if (asset?.id) await api.put(`/assets/${asset.id}`, payload);
      else await api.post("/assets", payload);
      toast.success(asset ? "Asset updated" : "Asset added");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-2xl card-soft p-6 my-8 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-slate-900 dark:text-white">
              {asset ? "Edit asset" : "Add new asset"}
            </h2>
            <button
              onClick={onClose}
              data-testid="asset-modal-close"
              className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <form onSubmit={onSubmit} className="space-y-3">
            <Field label="Name" required>
              <input
                data-testid="asset-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-soft"
                placeholder="e.g. HDFC Equity Fund"
              />
            </Field>
            <Field label="Category" required>
              <select
                data-testid="asset-category-select"
                value={form.category}
                onChange={(e) => onCategoryChange(e.target.value)}
                className="input-soft"
              >
                {ADDABLE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              {showPurchase && (
                <Field
                  label={
                    cfg.purchaseLabel ||
                    (isPerUnit(form.category)
                      ? "Purchase price per unit (₹)"
                      : "Purchase value (₹)")
                  }
                >
                  <MoneyInput
                    value={form.purchase_value}
                    onValueChange={(raw) =>
                      setForm({ ...form, purchase_value: raw })
                    }
                    placeholder="0"
                  />
                </Field>
              )}
              {showValue && (
                <Field
                  label={
                    isPerUnit(form.category)
                      ? "Current value per unit (₹)"
                      : "Current Value (₹)"
                  }
                >
                  <MoneyInput
                    data-testid="asset-value-input"
                    value={form.value}
                    onValueChange={(raw) => setForm({ ...form, value: raw })}
                    placeholder="0"
                  />
                </Field>
              )}
            </div>
            <p className="-mt-1 text-[11px] text-slate-400">
              {isPerUnit(form.category)
                ? "For gold & silver, enter the price per unit — total value is quantity × price."
                : "Optional. If left blank we fall back to the purchase value for calculations."}
            </p>

            {showPurchase && (
              <DateField
                label="Purchase / Acquisition Date"
                value={form.purchase_date}
                open={pickers.purchase}
                onToggle={() => setPickers({ purchase: !pickers.purchase })}
                onSelect={(d) => {
                  setForm({ ...form, purchase_date: d });
                  setPickers({});
                }}
                hint="Used for CAGR and capital-gains holding period."
              />
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label={cfg.returnLabel || "Expected Return %"}>
                <input
                  data-testid="asset-return-input"
                  type="number"
                  step="0.1"
                  value={form.expected_return}
                  onChange={(e) =>
                    setForm({ ...form, expected_return: e.target.value })
                  }
                  className="input-soft"
                  placeholder="12"
                />
              </Field>
              {showQuantity && (
                <Field label={cfg.quantity}>
                  <input
                    type="number"
                    step="0.0001"
                    value={form.quantity}
                    onChange={(e) =>
                      setForm({ ...form, quantity: e.target.value })
                    }
                    className="input-soft"
                    placeholder="0"
                  />
                </Field>
              )}
            </div>

            {form.category === "mis" &&
              Number(form.purchase_value) > 0 &&
              Number(form.expected_return) > 0 && (
                <div className="rounded-xl border border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2.5 text-sm">
                  <span className="text-emerald-700 dark:text-emerald-300">
                    Estimated monthly income{" "}
                    <b>
                      {fmtINR(
                        (Number(form.purchase_value) *
                          Number(form.expected_return)) /
                          1200,
                      )}
                    </b>{" "}
                    ·{" "}
                    {fmtINR(
                      (Number(form.purchase_value) *
                        Number(form.expected_return)) /
                        100,
                    )}
                    /yr
                  </span>
                </div>
              )}

            {showSIP && (
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Ongoing contributions
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={cfg.sipLabel || "Monthly contribution (₹)"}>
                    <MoneyInput
                      data-testid="asset-monthly-input"
                      value={form.monthly_contribution}
                      onValueChange={(raw) =>
                        setForm({ ...form, monthly_contribution: raw })
                      }
                      placeholder="0"
                    />
                  </Field>
                  <Field label="Annual step-up %">
                    <input
                      data-testid="asset-stepup-input"
                      type="number"
                      step="0.5"
                      value={form.contribution_step_up}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          contribution_step_up: e.target.value,
                        })
                      }
                      className="input-soft"
                      placeholder="0"
                    />
                  </Field>
                </div>
              </div>
            )}

            <Field label="Institution / Holder">
              <input
                data-testid="asset-institution-input"
                value={form.institution}
                onChange={(e) =>
                  setForm({ ...form, institution: e.target.value })
                }
                className="input-soft"
                placeholder="e.g. HDFC AMC"
              />
            </Field>

            <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
              <Checkbox
                data-testid="asset-liquid-toggle"
                checked={!!form.is_liquid}
                onCheckedChange={(checked) =>
                  setForm({ ...form, is_liquid: checked === true })
                }
              />

              <span>
                This is a liquid asset (counts toward Available Liquid Wealth)
              </span>
            </label>

            {/* Tenure / maturity — shown by default for FD/RD/Bonds, optional otherwise */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-900 dark:text-white">
                    Has Tenure / Maturity?
                  </div>
                  <div className="text-xs text-slate-500">
                    For FD, RD, bonds and any maturing instrument.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, has_tenure: !showTenure })}
                  className={`relative h-6 w-11 rounded-full transition ${
                    showTenure
                      ? "bg-emerald-500"
                      : "bg-slate-300 dark:bg-slate-700"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition ${
                      showTenure ? "left-5" : "left-0.5"
                    }`}
                  />
                </button>
              </div>

              {showTenure && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                  <DateField
                    label="Start Date"
                    value={form.start_date}
                    open={pickers.start}
                    onToggle={() => setPickers({ start: !pickers.start })}
                    onSelect={(d) => {
                      setForm({ ...form, start_date: d });
                      setPickers({});
                    }}
                  />
                  <DateField
                    label="Maturity Date"
                    value={form.maturity_date}
                    open={pickers.maturity}
                    onToggle={() => setPickers({ maturity: !pickers.maturity })}
                    onSelect={(d) => {
                      setForm({ ...form, maturity_date: d });
                      setPickers({});
                    }}
                  />
                  <Field label="Tenure (Years)">
                    <input
                      type="number"
                      min="0"
                      value={form.tenure_years}
                      onChange={(e) =>
                        setForm({ ...form, tenure_years: e.target.value })
                      }
                      className="input-soft"
                      placeholder="5"
                    />
                  </Field>
                  <p className="sm:col-span-2 text-[11px] text-slate-400">
                    Leave tenure blank to compute it from start → maturity date.
                    If no start date is given, <b>today's date</b> is used.
                  </p>
                </div>
              )}
            </div>

            <Field label="Notes">
              <textarea
                data-testid="asset-notes-input"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="input-soft min-h-[60px]"
                rows={2}
              />
            </Field>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="asset-save-btn"
                disabled={saving}
                className="btn-primary flex-1"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function AssetViewModal({ asset, onClose }) {
  const growth = cagr(asset.purchase_value, asset.value, asset.purchase_date);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
        <div className="w-full max-w-xl card-soft p-6 my-8 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
                {asset.name}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {CATEGORY_LABEL[asset.category] || asset.category}
                {isSynthetic(asset) && " · from Insurance"}
              </p>
            </div>
            <button
              onClick={onClose}
              className="grid h-8 w-8 place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ViewField label="Current Value" value={fmtINR(assetTotal(asset))} />
            <ViewField
              label="Expected Return"
              value={asset.expected_return ? `${asset.expected_return}%` : "—"}
            />
            {isPerUnit(asset.category) && Number(asset.quantity) > 0 && (
              <ViewField label="Price per unit" value={fmtINR(asset.value)} />
            )}
            <ViewField
              label="Purchase Value"
              value={assetInvested(asset) ? fmtINR(assetInvested(asset)) : "—"}
            />
            <ViewField
              label="Purchase Date"
              value={asset.purchase_date || "—"}
            />
            {growth !== null && (
              <ViewField
                label="Annualised Return (CAGR)"
                value={`${growth.toFixed(1)}%`}
              />
            )}
            {asset.quantity && (
              <ViewField label="Quantity" value={asset.quantity} />
            )}
            <ViewField label="Institution" value={asset.institution || "—"} />
            <ViewField
              label="Has Tenure"
              value={asset.has_tenure ? "Yes" : "No"}
            />
            {asset.has_tenure && (
              <>
                <ViewField label="Start Date" value={asset.start_date || "—"} />
                <ViewField
                  label="Maturity Date"
                  value={asset.maturity_date || "—"}
                />
                <ViewField
                  label="Tenure"
                  value={
                    asset.tenure_years ? `${asset.tenure_years} Years` : "—"
                  }
                />
              </>
            )}
            {Number(asset.monthly_contribution) > 0 && (
              <ViewField
                label="Monthly Contribution"
                value={fmtINR(asset.monthly_contribution)}
              />
            )}
          </div>

          <div className="mt-5">
            <ViewField label="Notes" value={asset.notes || "—"} full />
          </div>
        </div>
      </div>
    </div>
  );
}

// Thin wrapper over the shared, cross-browser DateField (keeps this file's
// call signature; open/onToggle are now handled internally by UiDateField).
function DateField({ label, value, onSelect, hint }) {
  return (
    <Field label={label}>
      <UiDateField value={value || ""} onChange={(v) => onSelect(v)} />
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </Field>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-slate-700 dark:text-slate-300">
        {label}
        {required && <span className="text-rose-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

function ViewField({ label, value, full = false }) {
  return (
    <div className={full ? "" : ""}>
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">
        {label}
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-4 py-3 text-sm text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-900/40">
        {value}
      </div>
    </div>
  );
}
